import React from "react";
import { BreadcrumbItem, Container, Col, Row, Nav, NavItem } from "reactstrap";
import { Component as Form } from "@repoflow/form-schema";
import * as Notify from "@repoflow/form-schema/notify";
import { Mutation, Button } from "@repoflow/components/section";
import ContentTabs, { useContentState } from "@repoflow/components/content-tabs";
import { Form as StepsForm, SubformsForm } from "components/steps-form";
import { Preview } from "components/steps-preview";
import { StepsHelp, SubformsHelp, ValuesHelp } from "components/types-help";
import * as widgets from "@shared/widgets/register";
import * as WorkflowQL from "queries/workflow";

export const Sidebar = () => null;

export const Indice = () => (
  <>
    <BreadcrumbItem>Edit</BreadcrumbItem>
  </>
);

// The field lists, hoisted OUT of the components.
//
// A `config` object literal in JSX is a new object on every render, and it is
// handed to the form's SchemaProvider, which rebuilds the widget tree
// underneath it. Every keystroke re-renders this component, so an inline config
// means the control being typed into is detached and replaced between
// keystrokes: the DOM ends up holding the text, React state does not, and the
// save writes the value the workflow already had. It reads as a save that
// worked against a screen that faithfully shows the old content.
const DETAILS_FIELDS = {
  "/Public/Workflow/Update": {
    fields: [
      "title",
      { property: "description", layout: "wide" },
      "tags",
    ],
  },
};

// The workflow editor: four tabs writing ONE value object, and one Save for
// all of them.
//
// Steps is what a workflow is for, so it is first. Details is the title and
// tags, which you set once. Subforms are the named field groups an `object`
// field on any step can ask for — declared once for the whole workflow, and
// next to Preview because Preview is where you see one rendered inline.
// Preview is the steps compiled into the forms a person filling the workflow
// in will see — and it is a tab of the editor rather than a pane beside it
// because a step is edited in a modal, and a second pane would sit behind
// that modal doing nothing while you type.
//
// One mutation, one Save, one value object, for the reason pagews' section
// editor gives: splitting the save would let somebody leave with the steps
// saved and the title not. Save therefore lives in the tab strip rather than
// under any one form, and the dirty-tracking Button reflects the workflow, not
// the tab.
//
// Saving does NOT navigate. You design a workflow in several saves, and
// landing on a read-only view each time makes the Preview tab useless.
export const Content = ({
  match: {
    params: { workflowId },
  },
}) => (
  <Mutation
    target={{ $ref: "/Public/Workflow/Update" }}
    mutation={WorkflowQL.edit}
    update={WorkflowQL.update({ workflowId })}
    // The editor stays where it is on save, so nothing remounts the route's
    // query and the workflow's own screen would otherwise keep showing the
    // steps this just replaced. The cache eviction next door clears the
    // LISTINGS, so a create shows up in the list; it cannot make an already
    // mounted query hand back a row it is still holding.
    refetch={[{ query: WorkflowQL.get, variables: { workflowId } }]}
    onMutate={({ handler, state: { values } }) => {
      // Only what the update input declares. The route's query seeds the form
      // with the whole row — workflowId, status, timestamps — and
      // WorkflowInputUpdate would refuse the extra fields.
      const { title, description, steps, subforms, tags } = values;
      handler({ variables: { workflowId, input: { title, description, steps, subforms, tags } } });
    }}
    onCompleted={() => {}}
    onError={(error) => {
      Notify.error(error.toString());
    }}
  >
    {/* A stable reference, never an inline arrow: `Mutation` renders what it
        is handed, so a fresh function here is a new component type on every
        render and remounts the whole editor under it — which throws away
        whatever is half-typed. */}
    {MutationHandler}
  </Mutation>
);

// The designer, over the one key it owns. The subforms are read here, never
// written: they are what an `object` field's select offers.
const StepsTab = ({ state }) => {
  const { values, setValues } = state;

  return (
    <>
      <StepsHelp />
      <StepsForm
        widgets={widgets}
        values={{ steps: values.steps || [] }}
        subforms={values.subforms || []}
        onChange={({ steps }) => setValues({ ...values, steps })}
      />
    </>
  );
};

// The subform designer, over its own key the same way.
const SubformsTab = ({ state }) => {
  const { values, setValues } = state;

  return (
    <>
      <SubformsHelp />
      <SubformsForm
        widgets={widgets}
        values={{ subforms: values.subforms || [] }}
        onChange={({ subforms }) => setValues({ ...values, subforms })}
      />
    </>
  );
};

// Title, description and tags. Small, and second, because you set them once.
const DetailsTab = ({ target, state, schema, mutate }) => (
  <Form
    schema={schema}
    target={target}
    widgets={widgets}
    values={state.values}
    onChange={state.setValues}
    onSubmit={mutate}
    config={DETAILS_FIELDS}
  />
);

// The steps as the forms they compile to — shared with the workflow's own
// screen, which previews the STORED steps through the same component. Here the
// steps are the unsaved ones in the form's state, so the preview follows what
// is being designed rather than what was last written.
const PreviewTab = ({ state }) => (
  <>
    <ValuesHelp />
    <Preview
      steps={state.values.steps || []}
      subforms={state.values.subforms || []}
      widgets={widgets}
      empty="No steps yet. Add one on the Steps tab and it will be previewed here."
    />
  </>
);

const MutationHandler = (props) => {
  // Which tab is open. Held here so that a save, which re-renders the editor,
  // does not send you back to the first tab.
  const tabs = useContentState({ selected: 0 });

  const items = [
    { label: "Steps", content: <StepsTab {...props} /> },
    { label: "Details", content: <DetailsTab {...props} /> },
    { label: "Subforms", content: <SubformsTab {...props} /> },
    { label: "Preview", content: <PreviewTab {...props} /> },
  ];

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <div className="float-right">
            <Nav tabs>
              <NavItem>
                {/* In the tab strip rather than under the form, because there
                    is no single form: three tabs write the same value object,
                    and a Save at the bottom of one of them would read as saving
                    only that one. */}
                <Button
                  enabledClass="nav-link text-success"
                  disabledClass="nav-link text-muted"
                >
                  Save
                </Button>
              </NavItem>
            </Nav>
          </div>

          <ContentTabs control={tabs} tabs={items} />
        </Col>
      </Row>
    </Container>
  );
};
